import type { PersistedState } from '@abs/shared-types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createCourseSlice, type CourseSlice } from './courseSlice';
import { createPolicySlice, type PolicySlice } from './policySlice';
import { createAbsenceSlice, type AbsenceSlice } from './absenceSlice';
import { createSettingsSlice, type SettingsSlice } from './settingsSlice';
import { createBudgetAdjustmentSlice, type BudgetAdjustmentSlice } from './budgetAdjustmentSlice';
import { createParticipationSlice, type ParticipationSlice } from './participationSlice';
import { createFeedbackSlice, type FeedbackSlice } from './feedbackSlice';
import { createAlertSlice, type AlertSlice } from './alertSlice';
import { saveState, loadState, clearState } from '@/persistence/db';

export type AppStore = CourseSlice & PolicySlice & AbsenceSlice & SettingsSlice &
  BudgetAdjustmentSlice & ParticipationSlice & FeedbackSlice & AlertSlice & {
  /** Import state atomically — replaces all data */
  importState: (payload: PersistedState) => void;
  /** Hydrate store from IndexedDB */
  hydrate: () => Promise<void>;
};

let writeTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Serialize current store state to PersistedState format.
 */
function serializeState(state: AppStore): PersistedState {
  return {
    schemaVersion: 2,
    courses: state.courses,
    policies: state.policies,
    absenceEntries: state.entries,
    terms: state.terms,
    settings: state.settings,
    budgetAdjustments: state.budgetAdjustments,
    participationEntries: state.participationEntries,
    feedbackEntries: state.feedbackEntries,
    exportedAt: new Date().toISOString(),
  };
}

/**
 * Debounced write to IndexedDB — fires 500ms after the last state change.
 */
function debouncedWrite(state: AppStore): void {
  if (writeTimeout !== null) {
    clearTimeout(writeTimeout);
  }
  writeTimeout = setTimeout(() => {
    const data = serializeState(state);
    saveState(data).catch((err) => {
      console.error('[Absence Budget] Failed to persist state:', err);
    });
  }, 500);
}

/**
 * Create the combined Zustand store with all slices.
 */
export const useAppStore = create<AppStore>()(
  devtools(
    (...a) => ({
      ...createCourseSlice(...a),
      ...createPolicySlice(...a),
      ...createAbsenceSlice(...a),
      ...createSettingsSlice(...a),
      ...createBudgetAdjustmentSlice(...a),
      ...createParticipationSlice(...a),
      ...createFeedbackSlice(...a),
      ...createAlertSlice(...a),

      importState: (payload) => {
        const set = a[0];
        set({
          courses: payload.courses,
          policies: payload.policies,
          entries: payload.absenceEntries,
          terms: payload.terms,
          settings: payload.settings,
          selectedCourseId: null,
          budgetAdjustments: payload.budgetAdjustments ?? [],
          participationEntries: payload.participationEntries ?? [],
          feedbackEntries: payload.feedbackEntries ?? [],
        });
      },

      hydrate: async () => {
        try {
          const data = await loadState();
          if (data) {
            const set = a[0];
            set({
              courses: data.courses,
              policies: data.policies,
              entries: data.absenceEntries,
              terms: data.terms,
              settings: data.settings,
              selectedCourseId: null,
              budgetAdjustments: data.budgetAdjustments ?? [],
              participationEntries: data.participationEntries ?? [],
              feedbackEntries: data.feedbackEntries ?? [],
            });
          }
        } catch (err) {
          console.error('[Absence Budget] Failed to hydrate store, resetting:', err);
          await clearState();
        }
      },
    }),
    { name: 'Absence Budget Store' },
  ),
);

// Subscribe to state changes for persistence
useAppStore.subscribe((state) => {
  debouncedWrite(state as AppStore);
});
